(ns app.front.view.party.common
  (:require [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.state-machine :as sm]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.ui.util :as uiu]
            [app.front.util :as futil]
            [app.front.validate :as validate :refer [field-invalid-text
                                                     field-invalid?]]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [goog.string :as gstr]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]))

(def modify-application-roles
  "Roles that should have modify application permission by default."
  #{:role/property-owner :role/applicant :role/agent})

(defn roles-requiring-modify-application [required-sign-off-roles]
  (into modify-application-roles (keep :party-role/key) required-sign-off-roles))

(def base-model {:party/party-roles [[validate/NotEmptyList :validate/role-not-selected]]})

(def invite-model
  (merge base-model
         {:party/email [[validate/Email :validate/wrong-email-address]]}))

(def form-model
  (merge base-model
         {:user/first-name                 [[validate/NonBlankString :validate/blank-first-name]]
          :user/last-name                  [[validate/NonBlankString :validate/blank-last-name]]
          :user/street-address             [[validate/NonBlankString :validate/blank-street-address]]
          :user/unit-number                [[validate/BlankOrString]]
          :user/municipality               [[validate/NonBlankString :validate/blank-municipality]]
          :user/province                   [[validate/NonBlankString :validate/blank-province]]
          :user/corporation-or-partnership [[validate/BlankOrString]]
          :user/postal-code                [[validate/NonBlankString :validate/blank-postal-code]]
          :user/mobile-phone               [[validate/Phone :validate/wrong-mobile-phone-format]]
          :user/other-phone                [[validate/BlankOrPhone :validate/wrong-phone-format]]
          :party/email                     [[validate/BlankOrEmail :validate/wrong-email-address]]}))

(defn model [{::keys [manual-mode?]}]
  (if manual-mode?
    form-model
    invite-model))

(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::set-doc-value    ::client-validation
                        ::update-doc-value ::client-validation
                        ::try-submit       ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-errors  ::server-errors
                        ::submit-success ::done}})

(def update-next-state (sm/make-update-next-state state-machine
                                                  [::document ::state]))

(defn get-page-kind [db]
  (get-in db [::document ::page-kind]))

(def manual-party-fields {:user/first-name                 ""
                          :user/last-name                  ""
                          :user/street-address             ""
                          :user/unit-number                ""
                          :user/postal-code                ""
                          :user/municipality               ""
                          :user/province                   ""
                          :user/corporation-or-partnership ""
                          :user/mobile-phone               ""
                          :user/other-phone                ""})

(defn party-role-label
  "Returns the localized label for the given party role.
   Uses subscriptions, so only use in reactive contexts (other subscriptions, views)."
  [party-role]
  (role-model/party-role-name party-role (<sub [:language])))

(defn reset-db
  "Bring the db to the initial state"
  [db]
  (assoc db ::document (merge manual-party-fields
                              {:party/email       ""
                               :party/permissions party-model/default-invite-permissions
                               :party/party-roles []
                               :invite/message    ""
                               ::state            nil
                               ::errors           {}
                               ::server-errors    nil
                               ::show-errors?     false
                               ::cr-party-role    nil})))

(defn submit-signal [db]
  (let [form (::document db)]
    (if (::manual-mode? form)
      [:process.signal/add-party
       (-> form
           (select-keys [:user/first-name
                         :user/last-name
                         :user/street-address
                         :user/postal-code
                         :user/municipality
                         :user/province
                         :user/corporation-or-partnership
                         :user/mobile-phone
                         :party/party-roles])
           (p/assoc-if
             :party/email (str-util/blank->nil (:party/email form))
             :user/unit-number (not-empty (:user/unit-number form))
             :user/other-phone (not-empty (:user/other-phone form))
             :party/permissions (:party/permissions form)
             :invite/message (:invite/message form)
             ;; nil (key absent) keeps the default backend behaviour: email => invite
             :party/send-invite? (:party/send-invite? form)))]
      [:process.signal/invite-party
       (-> form
           (select-keys [:party/email
                         :party/party-roles
                         :party/permissions
                         :invite/message]))])))

(def submit-success-message
  {:add    :party/added
   :invite :party/invited})

(rf/reg-event-fx ::submit-failure.bad-request
  (fn [{:keys [db _]} [event tr status body request-id]]
    (js/console.error "::submit-failure" status body)
    {:db       (-> db
                   (assoc-in [::document ::server-errors] (tr [(:error body)]))
                   (update-next-state event))
     :dispatch [:notifications/add
                :toast
                :error
                :error/error
                (:error body)
                request-id]}))

(rf/reg-event-fx ::submit-failure.forbidden
  (fn [{:keys [db _]} [event status body request-id]]
    (js/console.error "::submit-failure" status body)
    {:db       (update-next-state db event)
     :dispatch [:notifications/add
                :toast
                :error
                :error/error
                :error/forbidden
                request-id]}))

(rf/reg-event-fx ::submit-failure.error
  ;; Overriding :on-error replaces comm's default error handling, so keep its console logging.
  (fn [{:keys [db]} [event on-failure status]]
    (js/console.error "::submit-failure.error" status)
    {:db         (update-next-state db event)
     :dispatch-n [[:notifications/add
                   :toast
                   :error
                   :error/connection-error-title
                   :error/connection-error-description]
                  (into on-failure [status nil])]}))

(rf/reg-event-fx ::submit-finished
  (fn [_ [_ on-failure status body]]
    (when (not= 200 status)
      {:dispatch (into on-failure [status body])})))

(rc/reg-chain-named
  ::try-submit
  (fn [{:keys [db]} [event tr target-id opts]]
    (let [_          (::document db)
          on-failure (:on-failure opts)]
      (p/assoc-if
        {:db           (update-next-state db event)
         :comm/command (p/assoc-if
                         {:command        :process/apply-signal
                          :data           {:process/signal (submit-signal db)
                                           :entity-id      target-id}
                          :on-bad-request [::submit-failure.bad-request tr]
                          :on-forbidden   [::submit-failure.forbidden]}
                         :on-error         (when on-failure
                                             [::submit-failure.error on-failure])
                         :finally-dispatch (when on-failure
                                             [::submit-finished on-failure]))}
        :fx (when-let [on-start (:on-start opts)]
              [[:dispatch on-start]]))))
  ::submit-success
  (fn [{:keys [_ db]} [_ _ _ opts status response]]
    (let [page-kind (get-page-kind db)]
      {:db         (reset-db db)
       :dispatch-n (cond-> [[::entities/apply-changes status response]
                            [:notifications/add
                             :toast
                             :success
                             :success
                             (or (:success-message opts) (submit-success-message page-kind))]
                            [::close]]
                     (:on-success opts) (conj (into (:on-success opts) [status response])))})))

(rf/reg-event-db ::init
  (fn [db [_ page-kind & [initial-opts]]]
    (-> (reset-db db)
        (update ::document merge initial-opts)
        (assoc-in [::document ::page-kind] page-kind)
        (update-next-state ::init))))

(rf/reg-event-db ::init-assign-role
  (fn [db [_ role-id]]
    (-> (reset-db db)
        (assoc-in [::document ::page-kind] :assign)
        (assoc-in [::document ::assign-role] role-id)
        (update-next-state ::init))))

(rf/reg-event-db ::init-add-from-cr
  (fn [db [_ role-id]]
    (-> (reset-db db)
        (assoc-in [::document ::page-kind] :invite)
        (assoc-in [::document ::cr-party-role] role-id)
        (assoc-in [::document :party/type] :party.type/registered-contractor)
        (update-next-state ::init))))

(rf/reg-event-db ::reset-manual-party-fields
  (fn [db _]
    (update db ::document merge manual-party-fields)))

(rf/reg-event-fx ::close
  (fn [{:keys [db]} _]
    {:db (-> (reset-db db)
             (update ::document dissoc ::page-kind))}))

(rf/reg-event-db ::set-doc-value
  (fn [db [event path value]]
    (-> (if (nil? value)
          (p/dissoc-in db (into [::document] path))
          (assoc-in db (into [::document] path) value))
        (update ::document dissoc ::server-errors)
        (update-next-state event))))

(rf/reg-event-db ::update-doc-value
  (fn [db [event path f value]]
    (-> db
        (update-in (into [::document] path) f value)
        (update ::document dissoc ::server-errors)
        (update-next-state event))))

(rf/reg-event-db ::dissoc-doc-path
  (fn [db [_ path]]
    (p/dissoc-in db (into [::document] path))))

(rf/reg-sub ::document
  (fn [db [_ path f]]
    (let [doc     (::document db)
          sub-doc (if path (get-in doc path) doc)]
      (if f (f sub-doc) sub-doc))))

(rf/reg-sub ::validator
  :<- [::document]
  (fn [document _]
    #(validate/validate-field % document (model document))))

(defn validates? [db]
  (empty? (validate/validate-form (::document db) (model (::document db)))))

(rf/reg-sub ::form-valid?
  :<- [::document]
  (fn [document]
    (empty? (validate/validate-form document (model document)))))

(rf/reg-sub ::field-invalid?
  :<- [::validator]
  (fn [validator [_ field]]
    (some? (validator field))))

(defn handle-validate-fields
  [{:keys [db]} _]
  {:dispatch (if (validates? db)
               [::no-errors]
               [::still-errors])})

(rf/reg-event-fx ::submit-clicked
  (fn [{:keys [db]} [_ tr target-id opts]]
    {:db       (assoc-in db [::document ::show-errors?] true)
     :dispatch (if-not (validates? db)
                 [::validate-fields]
                 [::try-submit tr target-id opts])}))

(rf/reg-event-fx ::validate-fields handle-validate-fields)
(rf/reg-event-db ::no-errors update-next-state)
(rf/reg-event-db ::still-errors update-next-state)

(rf/reg-sub ::document-party-roles
  :<- [::document [:party/party-roles]]
  :<- [::ws-subs/party-roles]
  (fn [[role-ids party-roles]]
    (->> role-ids
         (map #(p/find-first party-roles {:db/id %}))
         set)))

(rf/reg-sub ::document-party-role-count
  :<- [::document [:party/party-roles]]
  :-> count)


(defn party-operation-permitted? [process page party-type role-id]
  (process/signal-permitted?
    process
    (case page
      :edit
      [:process.signal/add-party-role {:role-id role-id}]
      :add
      ;; business form can be add or invite depending on a by then hidden checkbox
      (case party-type
        :party.type/business [:process.signal/add-existing-business {:party/party-roles #{role-id}}]
        [:process.signal/add-party {:party/party-roles #{role-id}}])
      :invite
      (case party-type
        :party.type/business [:process.signal/add-existing-business
                              {:party/party-roles #{role-id}}]
        :party.type/registered-contractor [:process.signal/add-existing-contractor
                                           {:party/party-roles #{role-id}}]
        [:process.signal/invite-party {:party/party-roles #{role-id}}])
      ;; default
      [:process.signal/add-party-role {:role-id role-id}])))

(defn permitted-party-role-ids [roles process page party-type]
  (->> roles
       (map :db/id)
       (filter (partial party-operation-permitted? process page party-type))
       vec))

(rf/reg-sub ::available-roles
  (fn [[_ target-id] _]
    [(rf/subscribe [::ws-subs/applicable-party-role-ids])
     (rf/subscribe [::document [:party/party-roles]])
     (rf/subscribe [::ws-subs/party-roles])
     (rf/subscribe [::process/entity-process target-id])
     (rf/subscribe [::page-kind])
     (rf/subscribe [::party-type])])
  (fn [[all-role-ids current-role-ids party-roles process page party-type] _]
    (->> all-role-ids
         (remove (set current-role-ids))
         (filter (partial party-operation-permitted? process page party-type))
         (map #(p/find-first party-roles {:db/id %}))
         (sort-by party-role-label)
         seq)))

(defn- remove-pre-selected-roles [current-party-roles pre-selected]
  (let [difference                 (set/difference (set (map :db/id current-party-roles)) (set (map :db/id pre-selected)))
        remaining-selected-parties (filter #(contains? difference (:db/id %)) current-party-roles)]
    remaining-selected-parties))

(rf/reg-sub ::locked-selected-roles
  :<- [::document-party-roles]
  (fn [roles [_ reject-role-remove?]]
    (doall (filter #(reject-role-remove? (:db/id %)) roles))))

(rf/reg-sub ::unlocked-selected-roles
  (fn [[_ reject-role-remove?]]
    [(rf/subscribe [::locked-selected-roles reject-role-remove?])
     (rf/subscribe [::document-party-roles])])
  (fn [[locked-roles roles]]
    (remove-pre-selected-roles roles locked-roles)))

(rf/reg-sub ::page-kind get-page-kind)

(rf/reg-sub ::party-type
  :<- [::document]
  (fn [document _]
    (get document :party/type :party.type/person)))

(rf/reg-sub ::party-registration
  :<- [::document]
  :-> :party/registration)

(rf/reg-sub ::invite-open
  :<- [::page-kind]
  (fn [page-kind]
    (= :invite page-kind)))

(rf/reg-sub ::assign-open
  :<- [::page-kind]
  (fn [page-kind]
    (= :assign page-kind)))

(rf/reg-sub ::assign-role
  :<- [::document]
  :-> ::assign-role)

(rf/reg-event-fx ::apply-without-role
  (fn [_ [_ requirement-id target-id]]
    {:dispatch [::process/signal
                target-id
                [:process.signal/mark-party-requirement-deferred
                 {:requirement-id requirement-id}]]}))

(rf/reg-sub ::deferred-edit
  :-> ::deferred-edit)

(rf/reg-sub ::deferred-edit-in-progress?
  :<- [::deferred-edit]
  (fn [edit [_ path]]
    (contains? (get-in edit (butlast path)) (last path))))

(rf/reg-sub ::deferred-edit-modified?
  :<- [::document]
  :<- [::deferred-edit]
  (fn [[document edit] [_ path]]
    (not= (get-in document path)
          (get-in edit path))))

(rf/reg-event-db ::start-deferred-edit
  (fn [db [_ path]]
    (let [original-value (get-in (::document db) path)]
      (assoc-in db (concat [::deferred-edit] path) original-value))))

(rf/reg-event-db ::stop-deferred-edit
  (fn [db [_ path cancel?]]
    (let [deferred-path  (concat [::deferred-edit] path)
          original-value (get-in db deferred-path)]
      (cond-> (p/dissoc-in db deferred-path)
        cancel? (assoc-in (concat [::document] path) original-value)))))

(defn available-extra-permissions [workspace target-phase]
  ;; CE does not allow any extra permissions for parties
  (let [target-phase-type    (:entity/type target-phase)
        has-previous-phases? (->> (workspace-model/previous-phase-entity-types workspace target-phase-type)
                                  not-empty
                                  boolean)]
    (when (-> workspace :workspace/permit-type (not= :permit-type/CE))
      (concat [:party/modify-application]
              (when has-previous-phases?
                [:party/view-other-phases])
              (when (-> workspace :entity/parent)
                ;; has project
                [:party/add-application])))))


(defn has-extra-permission [target permission]
  (let [workspace (:entity/parent target)]
    (contains? (set (available-extra-permissions workspace target)) permission)))


(defn add-invalid-prop
  ([props tr validator field] (add-invalid-prop props tr validator field (constantly false)))
  ([props tr validator field disabled?]
   (let [enabled? (not (disabled? field))]
     (assoc props
            :invalid (field-invalid? (and enabled? (<sub [::document [::show-errors?]]))
                                     validator
                                     field)
            :invalid-text (field-invalid-text (and enabled? (<sub [::document [::show-errors?]]))
                                              validator
                                              field
                                              tr)))))

(defn extract-email-value [ev]
  (-> ev futil/tval str/lower-case))

(defn execute-if-specified [f & args]
  (when f (apply f args)))

(defn email-input [{:keys [class-name
                           tr
                           validator
                           required?
                           disabled?
                           field
                           label
                           helper-text
                           light
                           on-save-needed]}]
  (let [disabled? (or disabled? (constantly false))]
    [ui/text-input (-> {:id          "add-email"
                        :type        :email
                        :light       light
                        :label-text  (tr [label])
                        :helper-text helper-text
                        :placeholder (if (disabled? field) "" (tr [:placeholder/email]))
                        :required    required?
                        :disabled    (disabled? field)
                        :value       (or (<sub [::document [field]]) "")
                        :input-class class-name
                        :on-change   #(rf/dispatch-sync [::set-doc-value [field] (extract-email-value %)])
                        :on-blur     #(->> %
                                           extract-email-value
                                           (execute-if-specified on-save-needed field))}
                       (add-invalid-prop tr validator field disabled?))]))


(defn role-toggle-change-permissions-fn
  "Add or remove given permission according to the roles selected for the party."
  [modify-roles role-keys permission-to-change]
  (fn [operation {role-key :party-role/key}]
    (when (modify-roles role-key)
      (if (= operation :add)
        (>evt [::update-doc-value [:party/permissions] (partial cons permission-to-change)])
        (let [new-roles (-> role-keys set (disj role-key))]
          (when (not-any? modify-roles new-roles)
            (>evt [::update-doc-value [:party/permissions]
                   (fn [permissions] (remove #(= permission-to-change %) permissions))])))))))

(defn- role-selector-pills [{:keys [tr on-toggle reject-role-remove? target-id legend-tr-key]}]
  (let [reject-role-remove? (or reject-role-remove? (constantly false))
        pre-selected        (<sub [::locked-selected-roles reject-role-remove?])
        selected            (<sub [::unlocked-selected-roles reject-role-remove?])
        legend-text         (tr [(or legend-tr-key :party/role-legend)])]
    (if (<sub [::ws-subs/loading-config?])
      [:div
       [:legend.bx--label legend-text]
       (->> (repeat (<sub [::document-party-role-count]) (cc/button-skeleton {:small true}))
            (into [:div.flex.gap-2]))
       [:div.mt-2 (cc/button-skeleton {:small true})]]
      (when-let [available-roles (<sub [::available-roles target-id])]
        [ui-config-pills/config-pills
         (util/mmap {:values          available-roles
                     :add-button-text (tr [:party/role-add])
                     :val->str        party-role-label
                     :loading?        (<sub [::process/signal-processing?
                                             :process.signal/add-party-role
                                             :process.signal/remove-party-role])
                     :remove-item     (fn [role]
                                        (>evt [::update-doc-value [:party/party-roles] #(->> % (remove #{(:db/id role)}) vec)])
                                        (execute-if-specified on-toggle :remove role))
                     :add-item        (fn [role]
                                        (>evt [::update-doc-value [:party/party-roles] conj (:db/id role)])
                                        (execute-if-specified on-toggle
                                                              :add
                                                              (p/find-first available-roles {:db/id (:db/id role)})))
                     :id              "role-selector-pills"
                     :required?       true}
                    selected pre-selected tr legend-text)]))))

(defn role-selector [{:keys [tr validator on-toggle reject-role-remove? target-id legend-tr-key]}]
  (let [reject-role-remove? (or reject-role-remove? (constantly false))]
    [role-selector-pills (util/mmap tr validator on-toggle reject-role-remove? target-id legend-tr-key)]))

(defn permission-label [tr target permission]
  (->> (if (= :party/modify-application permission)
         [[:party/modify-phase-label]
          [(str/lower-case (tr [(:entity/type target)]))]]
         [[permission]])
       (apply tr)))

(rf/reg-sub ::workspace-phases
  :<- [::ws-subs/workspace]
  :-> party-model/phases-with-parties)

(rf/reg-sub ::view-other-phases-pre-checked
  :<- [::document [:party/permissions]]
  :<- [::workspace-phases]
  (fn [[permissions workspace-phases] [_ party]]
    (let [party-user    (-> party :party/user)
          current-phase (-> party :entity/parent)
          other-phases  (remove #(= % current-phase) workspace-phases)]
      (boolean (and (some #(= % :party/view-other-phases) permissions)
                    (not-empty other-phases)
                    (->> other-phases
                         (map #(party-model/parties-for-user party-user %))
                         (every? seq)))))))

(defn additional-permissions [{:keys [tr permissions disabled? target editing-party? party]}]
  (let [current-permissions           (<sub [::document [:party/permissions]])
        roles                         (<sub [::document-party-roles])
        modify-roles                  (roles-requiring-modify-application (<sub [::ws-subs/required-so-roles]))
        workspace-phases              (<sub [::workspace-phases])
        current-phase                 (-> party :entity/parent)
        other-phases                  (remove #(= % current-phase) workspace-phases)
        view-other-phases-pre-checked (<sub [::view-other-phases-pre-checked party])]
    (when (or (not disabled?)
              (seq current-permissions))
      (cc/form-group
        {:legend-text (tr [:party/permissions-label])
         :class-name  "mb-0"}
        [:div
         [:div.bx--form-item.pb-2
          (if disabled?
            (doall
              (for [permission current-permissions]
                [:div
                 {:key (str "permission-list-" permission)}
                 (cc/checkbox {:id         (str "list-permission-" permission)
                               :label-text (permission-label tr target permission)
                               :checked    true
                               :disabled   true})]))
            (doall
              (for [permission permissions]
                [:div
                 {:key (str "permission-" permission)}
                 (cc/checkbox {:id         (str "edit-permission-" (name permission))
                               :key        (str "checkbox-group-" permission)
                               :label-text (permission-label tr target permission)
                               :title      (permission-label tr target permission)
                               :disabled   (if editing-party?
                                             (boolean (or (not (<sub [::process/signal-permitted?
                                                                      target
                                                                      [:process.signal/edit-party-permissions]]))
                                                          (and (= permission :party/view-other-phases)
                                                               (or (empty? other-phases) view-other-phases-pre-checked))))
                                             (and (= permission :party/modify-application)
                                                  (->> roles
                                                       (map :party-role/key)
                                                       (some modify-roles)
                                                       boolean)))
                               :checked    (<sub [::document [:party/permissions] #(boolean (some (partial = permission) %))])
                               :on-change  (fn [checked]
                                             (let [f (if checked
                                                       (fn [permissions] (conj permissions permission))
                                                       (fn [permissions] (if editing-party? (disj permissions permission) (remove #(= permission %) permissions))))]
                                               (>evt [::update-doc-value [:party/permissions] f])))})])))]]))))

(defn invite-buttons-content [{:keys [kind tr target-id disabled?]}]
  [ui-action-buttons/action-buttons {:buttons [{:icon     i/remove
                                                :text     (tr [:party/cancel-button])
                                                :on-click #(>evt [::close])}
                                               {:icon      i/user-plus
                                                :on-click  #(>evt [::submit-clicked tr target-id])
                                                :disabled? (or disabled?
                                                               (not (<sub [::form-valid?])))
                                                :text      (tr [kind])}]}])

(defn assign-buttons [{:keys [tr]}]
  [:div
   [ui-action-buttons/action-buttons {:buttons [{:icon     i/remove
                                                 :text     (tr [:close])
                                                 :on-click #(>evt [::close])}]}]])

;; overriding-tr so that we can have municipality as a key and city as translation without changing municipality translation in the jsons.
(defn text-input [{:keys [name placeholder tr validator disabled? required? on-save-needed overriding-tr light]}]
  (let [field (keyword "user" name)]
    [ui/text-input (-> {:id          (str name)
                        :type        :text
                        :light       light
                        :disabled    (disabled? field)
                        :label-text  (if overriding-tr (tr [(keyword "party" overriding-tr)]) (tr [(keyword "party" name)]))
                        :placeholder (tr [placeholder])
                        :required    (or required? false)
                        :value       (or (<sub [::document [field]]) "")
                        :on-change   #(rf/dispatch-sync [::set-doc-value [field] (-> % .-target .-value)])
                        :on-blur     #(execute-if-specified on-save-needed field (-> % .-target .-value))}
                       (add-invalid-prop tr validator field disabled?))]))

(defn phone-input [{:keys [name tr validator disabled? required? on-save-needed light]}]
  (let [field (keyword "user" name)]
    [ui/intl-tel-input (-> {:id              (str name)
                            :type            :text
                            :light           light
                            :disabled        (disabled? field)
                            :label-text      (tr [(keyword "party" name)])
                            :placeholder     (tr [:placeholder/international-phone])
                            :required        required?
                            :value           (or (<sub [::document [field]]) "")
                            :callback        (fn [v]
                                               (rf/dispatch-sync [::set-doc-value [field] v])
                                               (execute-if-specified on-save-needed field v))
                            :initial-country (<sub [::front-config/country])}
                           (add-invalid-prop tr validator field disabled?))]))

(def editor-fields
  (uiu/wrap-in :div.bx--row :div.bx--col-xs-12.bx--col-md-3.mb-6))

(defn- deferred-email-input [{:keys [tr disabled?] :as context}]
  (let [editing? (<sub [::deferred-edit-in-progress? [:party/email]])]
    [email-input (merge (dissoc context :on-save-needed) ;; defer save to user's action
                        {:label       :party/email-optional
                         :helper-text (when editing? (tr [:party/email-invite-change]))
                         :field       :party/email
                         :disabled?   (fn [] (or (not editing?)
                                                 (disabled? :party/email)))})]))

(defn- deferred-email-actions [{:keys [tr on-save-needed]}]
  (let [editing?  (<sub [::deferred-edit-in-progress? [:party/email]])
        modified? (<sub [::deferred-edit-modified? [:party/email]])
        value     (<sub [::document [:party/email]])
        invalid?  (<sub [::field-invalid? :party/email])]
    (if editing?
      [:div.bx--form-item
       [:div.mt-7
        (cc/button {:kind       :tertiary
                    :class-name "mr-2"
                    :size       :small
                    :on-click   (fn [_]
                                  (>evt [::stop-deferred-edit [:party/email] true]))}
                   (tr [:cancel]))
        (cc/button {:kind     :primary
                    :size     :small
                    :disabled (or (not modified?)
                                  invalid?)
                    :on-click (fn [_]
                                (on-save-needed :party/email value)
                                (>evt [::stop-deferred-edit [:party/email] false]))}
                   (tr [:save]))]]
      [:div.bx--form-item (cc/button
                            {:icon-position :right
                             :kind          :tertiary
                             :class-name    "mt-7"
                             :size          :small
                             :icon          i/pen
                             :on-click      #(>evt [::start-deferred-edit [:party/email]])}
                            (tr [:party/edit-email]))])))

(defn common-editor-fields-v2
  "Renders common party editor fields.
   Context :show-email? is a boolean, while :disabled? is a function (fn [field] -> boolean).
   Context :deferred-email? (default true) renders the email as a deferred edit (pen + save/cancel,
   invite-resend hint). Pass false for creation flows where no invite is sent on email change
   (e.g. the application wizard)."
  [{:keys [show-email? disabled? deferred-email?]
    :or   {disabled? (constantly false) deferred-email? true}
    :as   context}]
  (let [context (assoc context :disabled? disabled?)]
    [:<>
     [editor-fields
      [text-input (merge context {:name        "first-name"
                                  :placeholder :placeholder/first-name
                                  :required?   true})]
      [text-input (merge context {:name        "last-name"
                                  :placeholder :placeholder/last-name
                                  :required?   true})]
      [text-input (merge context {:name        "street-address"
                                  :placeholder :placeholder/street
                                  :required?   true})]
      [text-input (merge context {:name        "unit-number"
                                  :placeholder :placeholder/unit})]]

     [editor-fields
      [text-input (merge context {:name          "municipality"
                                  :placeholder   :placeholder/municipality
                                  :required?     true
                                  :overriding-tr "city"})]
      [text-input (merge context {:name        "province"
                                  :placeholder :placeholder/province
                                  :required?   true})]
      [text-input (merge context {:name        "postal-code"
                                  :placeholder :placeholder/postal-code
                                  :required?   true})]
      [text-input (merge context {:name        "corporation-or-partnership"
                                  :placeholder :placeholder/company})]]

     [editor-fields
      [phone-input (merge context {:name      "mobile-phone"
                                   :required? true})]
      [phone-input (merge context {:name "other-phone"})]
      (when show-email?
        (if deferred-email?
          [deferred-email-input context]
          [email-input (merge context
                              {:label :party/email-optional
                               :field :party/email})]))
      (when (and deferred-email?
                 show-email?
                 (not (disabled? :party/email)))
        [deferred-email-actions context])]]))

(defn party-name-company-roles
  "Returns a string with party's full name, possible company and roles"
  [name company roles]
  (gstr/format "%s %s(%s)"
               name
               (if (not-empty company)
                 (str " / " company " ")
                 "")
               roles))
